// components/checkout/ReviewStep.tsx
import React from 'react';
import { Package, MapPin, CreditCard, Lock, ArrowLeft, Zap, Loader2, Building, Globe, User, Phone, CheckCircle, Clock, Truck } from 'lucide-react';
import DeliveryOptions from './DeliveryOptions';
import type { CartItem } from '../../types/cart';
import { formatPrice } from '../../utils/utilityFunctions';
import OrderItemsReview from './OrderItemsReview';
import { formatPhoneNumber } from '../../utils/PhoneFormatter';

interface ReviewStepProps {
    selectedAddressId: number | null;
    addresses: any[];
    newAddressData: any;
    user: any;
    cart: { items: CartItem[] };
    selectedDeliveryOption: any;
    setSelectedDeliveryOption: (option: any) => void;
    onEditAddress: () => void;
    onPlaceOrder: () => void;
    isSubmitting: boolean;
    onOptionsLoaded?: (options: any[]) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
    selectedAddressId,
    addresses,
    newAddressData,
    user,
    cart,
    selectedDeliveryOption,
    setSelectedDeliveryOption,
    onEditAddress,
    onPlaceOrder,
    isSubmitting,
    onOptionsLoaded
}) => {
    // Calculate order totals
    const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = selectedDeliveryOption?.fee || 0;
    const total = subtotal + deliveryFee;

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-sm sm:shadow-lg overflow-hidden">
                <div className="p-4 sm:p-8">
                    {/* Header */}
                    <div className="flex items-start mb-4 sm:mb-8">
                        <div className="p-2 sm:p-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg sm:rounded-xl mr-3">
                            <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Review Your Order</h2>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">Double-check before confirming</p>
                        </div>
                    </div>

                    {/* Review Sections */}
                    <div className="space-y-4 sm:space-y-6">
                        {/* Delivery Address Card */}
                        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl sm:rounded-2xl border border-blue-100 p-4 sm:p-6">
                            <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                                Delivery Address
                            </h3>
                            {selectedAddressId ? (
                                addresses.filter(a => a.id === selectedAddressId).map(address => (
                                    <div key={address.id} className="space-y-2 sm:space-y-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm sm:text-base font-medium text-gray-800 leading-tight">
                                                    {address.house_number} {address.address_line_1}
                                                </p>
                                                {address.address_line_2 && (
                                                    <p className="text-xs sm:text-sm text-gray-600 mt-1">{address.address_line_2}</p>
                                                )}
                                                <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 mt-1">
                                                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                    <span>{address.city_details?.name}</span>
                                                </div>
                                                {/* Contact info */}
                                                <div className="mt-3 space-y-1">
                                                    {/* Recipient */}
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                        <span className="text-xs sm:text-sm text-gray-600">{address.recipient_name || user?.full_name}</span>
                                                    </div>

                                                    {/* Contacts - side by side on desktop, stack on mobile */}
                                                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1">
                                                        {address.contact_number_1 && (
                                                            <div className="flex items-center gap-2">
                                                                <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                                <span className="text-xs sm:text-sm text-gray-600">
                                                                    {formatPhoneNumber(address.contact_number_1)}
                                                                    <span className="hidden sm:inline text-gray-400 ml-1">(primary)</span>
                                                                </span>
                                                            </div>
                                                        )}
                                                        {address.contact_number_2 && (
                                                            <div className="flex items-center gap-2">
                                                                <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                                <span className="text-xs sm:text-sm text-gray-600">
                                                                    {formatPhoneNumber(address.contact_number_2)}
                                                                    <span className="hidden sm:inline text-gray-400 ml-1">(secondary)</span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Verification badge */}
                                            <span className="flex-shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-green-200 text-green-800">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                <span className="hidden sm:inline">Verified</span>
                                                <span className="sm:hidden">✓</span>
                                            </span>
                                        </div>

                                        {/* Delivery note */}
                                        {address.delivery_note && (
                                            <div className="mt-2 pt-2 border-t border-blue-100">
                                                <p className="text-xs text-gray-700">
                                                    <span className="font-medium">Note:</span> {address.delivery_note}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm sm:text-base font-medium text-gray-800">
                                                {newAddressData.house_number} {newAddressData.address_line_1}
                                            </p>
                                            {newAddressData.address_line_2 && (
                                                <p className="text-xs sm:text-sm text-gray-600">{newAddressData.address_line_2}</p>
                                            )}
                                        </div>
                                        <span className="flex-shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800">
                                            <Clock className="w-3 h-3 mr-1" />
                                            <span className="hidden sm:inline">Needs Verification</span>
                                            <span className="sm:hidden">Pending</span>
                                        </span>
                                    </div>
                                    {newAddressData.delivery_note && (
                                        <div className="mt-2 pt-2 border-t border-blue-100">
                                            <p className="text-xs text-gray-700">
                                                <span className="font-medium">Note:</span> {newAddressData.delivery_note}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Order Items Section */}
                        <div className="bg-white rounded-xl sm:border border-gray-200 p-4 sm:p-6">
                            <OrderItemsReview
                                deliveryOption={selectedDeliveryOption}
                                cartItems={cart.items}
                            />
                        </div>

                        {/* Delivery Options */}
                        <DeliveryOptions
                            selectedAddressId={selectedAddressId}
                            cartItems={cart.items}
                            onSelect={setSelectedDeliveryOption}
                            selectedOption={selectedDeliveryOption}
                            onOptionsLoaded={onOptionsLoaded}
                        />

                        {/* Payment Method */}
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-6">
                            <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 sm:mb-6 flex items-center gap-2">
                                <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                                Payment Method
                            </h3>

                            {/* Payment Options - Compact */}
                            <div className="space-y-2 sm:space-y-3">
                                {/* Cash on Delivery - Available - Compact */}
                                <div
                                    onClick={() => { }}
                                    className="p-3 sm:p-4 rounded-lg border-2 border-blue-500 bg-blue-50 cursor-pointer"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                            {/* Dual icon container */}
                                            <div className="relative p-1.5 sm:p-2 bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                                                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                                                {/* Small cash overlay icon */}
                                                <div className="absolute -bottom-1 -right-1 bg-white border border-blue-200 rounded-full p-0.5">
                                                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-gray-900 text-sm sm:text-base">Pay on Delivery</p>
                                                <p className="text-gray-600 text-xs sm:text-sm truncate">
                                                    Cash or card accepted when your order arrives
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                            <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full whitespace-nowrap">
                                                Available
                                            </span>
                                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                                                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-blue-100">
                                        <p className="text-xs text-blue-700">
                                            ✓ View receipt online | ✓ Secure payment | ✓ No extra fees
                                        </p>
                                    </div>
                                </div>

                                {/* Coming Soon Options - Compact */}
                                <div className="space-y-2">
                                    {/* T&T Local Bank Linx Card - Compact */}
                                    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 opacity-75">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <div className="p-1.5 bg-gray-100 text-gray-400 rounded-lg flex-shrink-0">
                                                    <Building className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-900 text-sm">T&T Local Bank</p>
                                                    <p className="text-gray-600 text-xs truncate">Linx Card payment</p>
                                                </div>
                                            </div>
                                            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-bold rounded-full whitespace-nowrap">
                                                Soon
                                            </span>
                                        </div>
                                    </div>

                                    {/* VISA & Mastercard - Compact */}
                                    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 opacity-75">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <div className="p-1.5 bg-gray-100 text-gray-400 rounded-lg flex-shrink-0">
                                                    <Globe className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-900 text-sm">VISA & Mastercard</p>
                                                    <p className="text-gray-600 text-xs truncate">Credit/Debit card payment</p>
                                                </div>
                                            </div>
                                            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-bold rounded-full whitespace-nowrap">
                                                Soon
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Information - Compact */}
                            <div className="mt-3 sm:mt-4 p-3 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-100">
                                <div className="flex items-start gap-2">
                                    <Lock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-xs text-blue-700 flex-1">
                                        Your payment is secure. For cash on delivery, you can view your receipt online after delivery.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="px-4 sm:px-8 py-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200">
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                        <button
                            onClick={onEditAddress}
                            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3.5 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
                        >
                            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                            Edit Address
                        </button>
                        <button
                            onClick={onPlaceOrder}
                            disabled={isSubmitting || !selectedDeliveryOption}
                            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                                    Placing Order...
                                </>
                            ) : (
                                <>
                                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    <span className="hidden sm:inline">Place Order ({formatPrice(total)})</span>
                                    <span className="sm:hidden">Place Order</span>
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-center text-xs sm:text-sm text-gray-500 mt-2 sm:mt-4">
                        By placing your order, you agree to our Terms of Service
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReviewStep;